import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Tags, Plus, Trash2, Search, Loader2, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/toaster';
import { tagSetsApi, listTagSetsApi } from '@/lib/api';
import { useMyCapabilities } from '@/hooks/use-permissions';

interface ListTagSetsDialogProps {
  listId: string;
  listName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ListTagSetsDialog({
  listId,
  listName,
  open,
  onOpenChange,
}: ListTagSetsDialogProps) {
  const { data: capabilities } = useMyCapabilities();
  const canRenameTagSets = Boolean(capabilities?.['tag_sets.edit']);
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [renamingTagSet, setRenamingTagSet] = useState<{ id: string; name: string } | null>(null);
  const [renameValue, setRenameValue] = useState('');

  const { data: assignedTagSets = [], isLoading: isLoadingAssigned } = useQuery({
    queryKey: ['listTagSets', listId],
    queryFn: async () => {
      const response = await listTagSetsApi.getTagSets(listId);
      return response.data;
    },
    enabled: open,
  });

  const { data: allTagSets = [], isLoading: isLoadingAll } = useQuery({
    queryKey: ['tagSets'],
    queryFn: async () => {
      const response = await tagSetsApi.getAll();
      return response.data;
    },
    enabled: open,
  });

  const assignMutation = useMutation({
    mutationFn: (tagSetId: string) => listTagSetsApi.assign(listId, tagSetId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['listTagSets', listId] });
      queryClient.invalidateQueries({ queryKey: ['effectiveTags', listId] });
      toast({ title: 'Tag set assigned' });
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast({
        title: 'Failed to assign tag set',
        description: error.response?.data?.message || 'Please try again',
        variant: 'destructive',
      });
    },
  });

  const removeMutation = useMutation({
    mutationFn: (tagSetId: string) => listTagSetsApi.remove(listId, tagSetId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['listTagSets', listId] });
      queryClient.invalidateQueries({ queryKey: ['effectiveTags', listId] });
      toast({ title: 'Tag set removed' });
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast({
        title: 'Failed to remove tag set',
        description: error.response?.data?.message || 'Please try again',
        variant: 'destructive',
      });
    },
  });

  const renameMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      tagSetsApi.update(id, { name: name.trim() }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tagSets'] });
      queryClient.invalidateQueries({ queryKey: ['listTagSets', listId] });
      queryClient.invalidateQueries({ queryKey: ['effectiveTags', listId] });
      setRenamingTagSet(null);
      setRenameValue('');
      toast({ title: 'Tag set renamed' });
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast({
        title: 'Failed to rename tag set',
        description: error.response?.data?.message || 'Please try again',
        variant: 'destructive',
      });
    },
  });

  const assignedTagSetIds = useMemo(
    () => new Set(assignedTagSets.map((lts) => lts.tagSetId)),
    [assignedTagSets]
  );

  const availableTagSets = useMemo(() => {
    const unassigned = allTagSets.filter((ts) => !assignedTagSetIds.has(ts.id));
    if (!search.trim()) return unassigned;
    const searchLower = search.toLowerCase();
    return unassigned.filter(
      (ts) =>
        ts.name.toLowerCase().includes(searchLower) ||
        ts.description?.toLowerCase().includes(searchLower)
    );
  }, [allTagSets, assignedTagSetIds, search]);

  const isLoading = isLoadingAssigned || isLoadingAll;
  const isPending = assignMutation.isPending || removeMutation.isPending || renameMutation.isPending;

  const openRenameDialog = (tagSet: { id: string; name: string }) => {
    setRenamingTagSet({ id: tagSet.id, name: tagSet.name });
    setRenameValue(tagSet.name);
  };

  const submitRename = () => {
    if (!renamingTagSet) return;
    if (!renameValue.trim()) {
      toast({
        title: 'Invalid name',
        description: 'Tag set name cannot be empty.',
        variant: 'destructive',
      });
      return;
    }
    renameMutation.mutate({ id: renamingTagSet.id, name: renameValue });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-2xl max-h-[85vh] overflow-hidden !p-0 !gap-0 flex flex-col">
        <DialogHeader className="shrink-0 px-6 pt-6 pb-3">
          <DialogTitle className="flex items-center gap-2">
            <Tags className="h-5 w-5" />
            Tag Sets for {listName}
          </DialogTitle>
          <DialogDescription>
            Assign tag sets to make their tags available for tasks in this list.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="px-6 pb-6">
            <div className="space-y-4">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          </div>
        ) : (
          <div className="px-6 pb-6 overflow-y-auto flex-1">
            <div className="space-y-4">
              {/* Assigned tag sets */}
              <div className="rounded-lg border bg-muted/20 p-3">
                <h4 className="text-sm font-medium mb-2">Assigned Tag Sets</h4>
                {assignedTagSets.length === 0 ? (
                  <div className="text-sm text-muted-foreground py-4 text-center border rounded-lg">
                    No tag sets assigned yet
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[25vh] overflow-y-auto pr-1">
                    {assignedTagSets.map((lts) => (
                      <div
                        key={lts.id}
                        className="flex items-start justify-between gap-3 p-3 rounded-lg border bg-background"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="font-medium break-words leading-snug">{lts.tagSet.name}</p>
                          {lts.tagSet.description && (
                            <p className="text-xs text-muted-foreground break-words leading-snug mt-0.5">
                              {lts.tagSet.description}
                            </p>
                          )}
                          <p className="text-xs text-muted-foreground mt-1">
                            {lts.tagSet._count?.tags || 0} tag(s)
                          </p>
                        </div>
                        <div className="shrink-0 flex items-center gap-1">
                          {canRenameTagSets && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => openRenameDialog({ id: lts.tagSet.id, name: lts.tagSet.name })}
                              disabled={isPending}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            onClick={() => removeMutation.mutate(lts.tagSetId)}
                            disabled={isPending}
                          >
                            {isPending ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Available tag sets */}
              <div className="rounded-lg border bg-muted/20 p-3">
                <h4 className="text-sm font-medium mb-2">Available Tag Sets</h4>
                <div className="relative mb-2">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search tag sets..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-8 h-8 text-sm"
                  />
                </div>
                {availableTagSets.length === 0 ? (
                  <div className="text-sm text-muted-foreground py-4 text-center border rounded-lg">
                    {allTagSets.length === 0
                      ? 'No tag sets exist yet. Create some in the Tag Manager.'
                      : 'All tag sets are assigned to this list'}
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[30vh] overflow-y-auto pr-1">
                    {availableTagSets.map((ts) => (
                      <div
                        key={ts.id}
                        className="flex items-start justify-between gap-3 p-3 rounded-lg border bg-background hover:bg-muted/50 transition-colors"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="font-medium break-words leading-snug">{ts.name}</p>
                          {ts.description && (
                            <p className="text-xs text-muted-foreground break-words leading-snug mt-0.5">
                              {ts.description}
                            </p>
                          )}
                          <p className="text-xs text-muted-foreground mt-1">
                            {ts._count?.tags || 0} tag(s)
                          </p>
                        </div>
                        <div className="shrink-0 flex items-center gap-2">
                          {canRenameTagSets && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => openRenameDialog({ id: ts.id, name: ts.name })}
                              disabled={isPending}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-1"
                            onClick={() => assignMutation.mutate(ts.id)}
                            disabled={isPending}
                          >
                            {isPending ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <>
                                <Plus className="h-4 w-4" />
                                Assign
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </DialogContent>

      <Dialog
        open={!!renamingTagSet}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            setRenamingTagSet(null);
            setRenameValue('');
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Rename tag set</DialogTitle>
            <DialogDescription>Update the tag set name.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Input
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              placeholder="Tag set name"
              maxLength={100}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setRenamingTagSet(null);
                setRenameValue('');
              }}
            >
              Cancel
            </Button>
            <Button onClick={submitRename} disabled={!renameValue.trim() || renameMutation.isPending}>
              {renameMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
